import { Link } from 'react-router-dom';

import type { RosterDriver } from '../lib/api';
import { formatDate, fullName } from '../lib/format';
import './Horizon.css';

/**
 * The Compliance Horizon.
 *
 * Managing driver qualification is managing a rolling wall of expiry dates: every item in
 * every file runs out, and the job is seeing the wave before it lands. A status donut or a
 * bar chart of counts answers "how are we doing"; it does not answer "what is about to
 * break", which is the question that actually occupies this role.
 *
 * So the fleet is plotted on time. Each driver is one tick at their soonest due date. The
 * left-hand gutter is separate and deliberately unscaled — overdue has no useful distance,
 * it is simply past, and giving it proportional space would push everything else off the
 * chart the longer something stayed unresolved.
 *
 * Built in plain HTML and CSS rather than SVG: it reflows to a phone, the ticks are real
 * focusable links, and there is no chart library to keep in step with the palette.
 */

/**
 * Candidate windows, narrowest first, with the gridlines each one uses.
 *
 * The window adapts because a fixed 90 days renders an almost-empty chart for a carrier
 * whose files happen to be in good order — one tick in a wide empty band, taking up the
 * most prominent space on the page and saying nothing. The narrowest window that actually
 * shows most of the fleet's upcoming work is the useful one.
 */
const WINDOWS = [
  { days: 90, label: 'The next 90 days', gridlines: [0, 30, 60, 90] },
  { days: 180, label: 'The next 6 months', gridlines: [0, 60, 120, 180] },
  { days: 365, label: 'The next 12 months', gridlines: [0, 90, 180, 270, 365] },
] as const;

interface Props {
  drivers: RosterDriver[];
  /** The tenant's local date — the same value the engine evaluated against. */
  asOf: string;
}

interface Tick {
  driver: RosterDriver;
  daysOut: number;
  /** 0–100% across the horizon. */
  offset: number;
}

function daysBetweenIso(from: string, to: string): number {
  const a = Date.parse(`${from.slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${to.slice(0, 10)}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

export function Horizon({ drivers, asOf }: Props) {
  // The gutter test matches the ledger sentence above it: a driver whose file is deficient
  // for any reason, including an upload nobody has reviewed. An earlier version tested only
  // expired-or-missing here while the ledger counted all deficiencies, so the two numbers
  // on the same screen disagreed.
  const overdue = drivers.filter((d) => !d.compliant);
  const upcoming = drivers.filter((d) => d.compliant && d.nextDueAt);

  // Pick the narrowest window holding at least two thirds of the upcoming work.
  const chosen =
    WINDOWS.find((w) => {
      const inside = upcoming.filter((d) => daysBetweenIso(asOf, d.nextDueAt!) <= w.days).length;
      return upcoming.length === 0 || inside >= Math.ceil(upcoming.length * (2 / 3));
    }) ?? WINDOWS[WINDOWS.length - 1]!;

  const ticks: Tick[] = [];
  let beyond = 0;

  for (const driver of upcoming) {
    const daysOut = daysBetweenIso(asOf, driver.nextDueAt!);
    if (daysOut < 0) continue; // a compliant driver with a past date has nothing outstanding
    if (daysOut > chosen.days) {
      beyond++;
    } else {
      ticks.push({ driver, daysOut, offset: (daysOut / chosen.days) * 100 });
    }
  }

  ticks.sort((a, b) => a.daysOut - b.daysOut);

  const { gridlines } = chosen;
  const WINDOW_DAYS = chosen.days;
  // "Near" scales with the window so the emphasis keeps meaning something.
  const nearThreshold = Math.round(WINDOW_DAYS / 3);

  return (
    <section className="horizon card" aria-labelledby="horizon-heading">
      <div className="horizon-head">
        <div>
          <p className="eyebrow">{chosen.label}</p>
          <h2 id="horizon-heading" className="horizon-title">
            Compliance horizon
          </h2>
        </div>
        <p className="horizon-legend muted">
          Each mark is one driver, placed at their next deadline.
        </p>
      </div>

      <div className="horizon-body">
        {/* Overdue gutter — unscaled on purpose. */}
        <div className={`horizon-gutter${overdue.length > 0 ? ' is-active' : ''}`}>
          <span className="horizon-gutter-count tnum">{overdue.length}</span>
          <span className="horizon-gutter-label">
            {overdue.length === 1 ? 'file needs' : 'files need'}
            <br />
            fixing now
          </span>
          {overdue.length > 0 && (
            <ul className="horizon-gutter-list">
              {overdue.slice(0, 6).map((d) => (
                <li key={d.id}>
                  <Link to={`/drivers/${d.id}`}>{fullName(d)}</Link>
                </li>
              ))}
              {overdue.length > 6 && <li className="muted">+{overdue.length - 6} more</li>}
            </ul>
          )}
        </div>

        <div className="horizon-track-wrap">
          <div className="horizon-track">
            {gridlines.map((day) => (
              <div
                key={day}
                className="horizon-gridline"
                style={{ left: `${(day / WINDOW_DAYS) * 100}%` }}
              />
            ))}

            {ticks.map(({ driver, daysOut, offset }) => (
              <Link
                key={driver.id}
                to={`/drivers/${driver.id}`}
                className={`horizon-tick${daysOut <= nearThreshold ? ' is-near' : ''}`}
                style={{ left: `${offset}%` }}
                title={`${fullName(driver)} — ${formatDate(driver.nextDueAt)} (${daysOut} days)`}
              >
                <span className="sr-only">
                  {fullName(driver)}, next deadline {formatDate(driver.nextDueAt)}
                </span>
              </Link>
            ))}

            {ticks.length === 0 && overdue.length === 0 && (
              <p className="horizon-empty muted">
                Nothing falls due in this window.
              </p>
            )}
          </div>

          <div className="horizon-axis" aria-hidden="true">
            {gridlines.map((day) => (
              <span
                key={day}
                className="horizon-axis-mark tnum"
                style={{ left: `${(day / WINDOW_DAYS) * 100}%` }}
              >
                {day === 0 ? 'today' : day >= 365 ? '1y' : day % 30 === 0 && day >= 90 ? `${day / 30}mo` : `${day}d`}
              </span>
            ))}
          </div>
        </div>
      </div>

      {beyond > 0 && (
        <p className="horizon-beyond muted">
          <span className="tnum">{beyond}</span> more {beyond === 1 ? 'driver has' : 'drivers have'}{' '}
          nothing due in this window.
        </p>
      )}
    </section>
  );
}
